import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { feedbackAPI } from '../services/api';
import { 
  XMarkIcon, 
  PaperAirplaneIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const FeedbackDetail = ({ feedback, onClose, onUpdate }) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(feedback.status);
  const [localReplies, setLocalReplies] = useState(feedback.replies || []);

  // Sync local state when feedback prop changes
  useEffect(() => {
    setStatus(feedback.status);
    setLocalReplies(feedback.replies || []);
  }, [feedback]);

  const handleReply = async (e) => {
    e.preventDefault();

    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await feedbackAPI.replyToFeedback(feedback.id, replyMessage);
      toast.success('Reply sent successfully!');
      
      // Add the new reply to local state immediately
      const newReply = {
        id: response.data.reply.id,
        message: response.data.reply.message,
        replierName: response.data.reply.replierName,
        createdAt: response.data.reply.createdAt
      };
      
      const updatedReplies = [...localReplies, newReply];
      const newStatus = status === 'open' ? 'replied' : status;
      
      setLocalReplies(updatedReplies);
      setReplyMessage('');
      
      // Update status to 'replied' if it was 'open'
      if (status === 'open') {
        setStatus('replied');
      }
      
      // Notify parent component to update the feedback in the list
      if (onUpdate) {
        onUpdate({
          ...feedback,
          replies: updatedReplies,
          status: newStatus
        });
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(error.response?.data?.error || 'Failed to send reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await feedbackAPI.updateStatus(feedback.id, newStatus);
      setStatus(newStatus);
      toast.success('Status updated successfully');
      
      // Notify parent component to update the feedback status in the list
      if (onUpdate) {
        onUpdate({
          ...feedback,
          status: newStatus
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: 'Open' },
      replied: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircleIcon, label: 'Replied' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircleIcon, label: 'Closed' }
    };
    const badge = badges[status] || badges.open;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-indigo-600 text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Feedback Details</h2>
            <p className="text-sm text-indigo-100 mt-1">
              {feedback.recipientType === 'manager' ? 'From Team Member' : 'From User'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Sender Info */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feedback.isAnonymous ? 'Anonymous' : (feedback.senderName || 'Unknown')}
                  </h3>
                  {getStatusBadge(status)}
                </div>
                {!feedback.isAnonymous && feedback.senderEmail && (
                  <p className="text-sm text-gray-600">{feedback.senderEmail}</p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <span>To: {feedback.recipientType === 'manager' ? 'Manager' : 'Super Admin'}</span>
                  <span>•</span>
                  <span>{formatDate(feedback.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Message</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 whitespace-pre-wrap">{feedback.message}</p>
            </div>
          </div>

          {/* Replies */}
          {localReplies && localReplies.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Replies ({localReplies.length})
              </h4>
              <div className="space-y-4">
                {localReplies.map((reply) => (
                  <div key={reply.id} className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900">{reply.replierName || 'Unknown'}</span>
                        <span className="text-xs text-gray-500">(You)</span>
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(reply.createdAt)}</span>
                    </div>
                    <p className="text-gray-900 whitespace-pre-wrap">{reply.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reply Form */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Reply</h4>
            <form onSubmit={handleReply}>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none mb-3"
                placeholder="Type your reply here..."
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <label className="text-sm text-gray-700">Status:</label>
                  <select
                    value={status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="open">Open</option>
                    <option value="replied">Replied</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || !replyMessage.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                      Send Reply
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDetail;

